<script lang="ts">
	import { beforeUpdate } from 'svelte';
	//Formating
	import { format } from 'svelte-i18n';
	// Design system components
	import { Button, Text, Container, FullScreenContainer, Stack } from '@Shelly/shelly-design-system';
	//Components
	import TempSchemeChart from '../../../Chart/TempSchemeChart.svelte';
	import IntervalPreview from '../../components/IntervalPreview.svelte';
	// import IntervalPreview from '../../components/GenericView/IntervalPreview.svelte';

	import { capFirstChar, _deepClone, _deepCopyArr } from '../../../../helpers/utilities';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	import ChooseTemperature from '../Process/TemperatureAndIntervals/ChooseTemperature.svelte';
	import AddEditScheduleInterval from '../Process/TemperatureAndIntervals/AddEditScheduleInterval.svelte';
	//Helpers
	import { findEmptyInterval } from '../../helpers/intervals_helpers';
	import { translateThermostat } from '../../helpers/temp_scheme_to_ui';
	//Types
	import type {
		IChartTempSchedule,
		ITempSchemeObj,
		TempScheduleInterval,
		TempSchemeInterval,
	} from '../../../../types/TemperatureSchemeType';

	export let temp_scheme_object: ITempSchemeObj;

	let forceEnabled = true;

	const updateIntervals = (): IChartTempSchedule => {
		return translateThermostat(temp_scheme_object);
	};

	const onBaseTemperatureChange = (e: CustomEvent) => {
		let { min = 5, max = 35 } = temp_scheme_object._meta?.[data.units]?.[data.type];

		const value = Math.min(Number(max), Math.max(Number(min), e.detail.value));
		data.base = temp_scheme_object.deftemp = value;
	};

	const addInterval = () => {
		addEditInterval(-1);
	};

	const addEditInterval = (ind: number) => {
		selectIntervalID = ind;
		showAddEditInterval = true;
	};

	const deleteInterval = (ind: number) => {
		temp_scheme_object.intervals =
			[...temp_scheme_object.intervals.filter((_el, i) => i !== ind)] || [];
		initIntervals();
	};

	const closeModal = () => {
		showAddEditInterval = false;
	};

	const initIntervals = () => {
		data = updateIntervals();
		disabled = findEmptyInterval(data.intervals) == null;
	};

	const saveInterval = (e: CustomEvent) => {
		const interval: TempScheduleInterval = e.detail.interval;
		const days: number[] = interval.days.map((i: number) => (i == 0 ? 7 : i)).sort();
		const tempSchemeInterval: TempSchemeInterval = {
			indailyint: {
				dows: days.toString(),
				startat: interval.time[0],
				dur: interval.dur,
			},
			disabled: interval.disabled || false,
			tschem_temp: Number(interval.base),
		};
		if (selectIntervalID == -1) {
			temp_scheme_object.intervals.push(tempSchemeInterval);
		} else {
			temp_scheme_object.intervals[selectIntervalID] = tempSchemeInterval;
		}
		initIntervals();
		closeModal();
	};

	let showAddEditInterval = false;
	let data: IChartTempSchedule;
	let selectIntervalID = -1;
	let disabled = false;

	beforeUpdate(() => {
		if (temp_scheme_object?._meta?.type) {
			initIntervals();
		}
	});
</script>

{#if data}
	<Container padding="scrollPaddings">
		<Text class="mb-s" element="h2">
			{capFirstChar($format('base_temperature', { default: 'base temperature' }))}
		</Text>
		<ChooseTemperature
			units={data.units}
			value={data.base}
			mode={data.type}
			on:temp_change={(e) => onBaseTemperatureChange(e)}
		/>
		<Text class="mt-m" element="p" styleType="body-small">
			{$format('wizard_manual_change_base_temperature_description', {
				default:
					"* You can control manually the current interval temperature by the controllers from the drawer. If there isn't set a temperature interval the thermostat controllers will change the desired temperature for the thermostat. Which will be different than the base temperature from the wizard. If there are set multiple intervals from the wizard the controllers from the drawer  will change only the current interval only this time.",
			})}
		</Text>
		<hr class="mt-l mb-l" />
	</Container>
	<Container padding="scrollPaddings">
		<Text class="mb-s" element="h2">
			{capFirstChar($format('temperature_intervals'))}
		</Text>
		{#if data.intervals.length > 0}
			<Stack gap="s" class="mb-m">
				{#each data.intervals as interval, i (interval)}
					<IntervalPreview
						withControls={true}
						units={data.units}
						{interval}
						on:edit={() => addEditInterval(i)}
						on:delete={() => deleteInterval(i)}
					/>
				{/each}
			</Stack>
		{:else}
			<Text element="p" styleType="body-small" class="mb-m">
				{$format('not_specific_intervals_set', {
					default:
						'You have no specific intervals set. You can add the first one by simply clicking the button below.',
				})}
			</Text>
		{/if}
		<div class="text-center">
			<Button variant="secondary" size="m" {disabled} on:click={addInterval}>
				{$format('button_add_interval', {
					default: 'Add interval',
				})}
			</Button>
		</div>
	</Container>
	{#if data.intervals.length > 0}
		<Container padding="scrollPaddings">
			<hr class="mt-l mb-md-0 mb-sm-s" />
		</Container>

		<Container class="chart-scroll-container">
			<Text class="mb-s" element="h2">
				{capFirstChar($format('process_temperature_schedule_preview'))}
			</Text>
			<FullScreenContainer
				class="chart-full-screen-container"
				backgroundColor={$showNewLayout ? 'color-0' : undefined}
			>
				<Text styleType="h2" slot="title" class="text-capitalize" noWrap>
					{$format('process_temperature_schedule')}
				</Text>
				<TempSchemeChart {data} {forceEnabled} />
			</FullScreenContainer>
		</Container>
	{/if}

	{#if showAddEditInterval}
		<AddEditScheduleInterval
			{data}
			{temp_scheme_object}
			intervalID={selectIntervalID}
			on:cancel={closeModal}
			on:save={saveInterval}
		/>
	{/if}
{/if}
